import { fromCallback } from 'xstate';

import { AppointmentFileTypesLookup } from '@eluve/graphql-types';
import { isAudioFileChunkFile } from '@eluve/utils';

import type {
  AudioSource,
  AudioSourceConfig,
} from '../audio-sources/audio-source';
import { BlobAudioSource } from '../audio-sources/blob-audio-source';
import { PcmAudioSource } from '../audio-sources/pcm-audio-source';
import { MsgFromMain, MsgToMain } from '../workers/messages-types';

import { type AudioFileSystemEvents } from './audioFileSystem.types';
import {
  type MediaRecorderEvents,
  MediaRecorderInput,
} from './mediaRecorder.types';
import { AudioHealthCheck } from './periodicAudioHealthCheck';

// Check if AudioEncoder is supported (for fallback detection)
const isAudioEncoderSupported = (): boolean => {
  return typeof AudioEncoder !== 'undefined';
};

export const webWorkerAudioActor = fromCallback<
  MediaRecorderEvents,
  MediaRecorderInput
>(({ input, sendBack, receive }) => {
  const safeSendBack = sendBack as (
    event: MediaRecorderEvents | AudioFileSystemEvents,
  ) => void;

  const {
    audioPacketSubject,
    deviceId,
    logger,
    MuteDetector,
    tenantId,
    userId,
    db,
    recordingStartedAt,
    appointmentId = '',
    chunkDurationInSeconds,
    channelCount = 1,
    initialFileName,
    resumableUploadSessionUri,
    nextChunkResumableUploadSessionUri,
    datadogEnv,
    serviceName,
    supportedAudioFormat,
  } = input;

  // Determine which mode to use based on AudioEncoder support
  const useAudioEncoder = isAudioEncoderSupported();
  const mode: 'pcm' | 'blob' = useAudioEncoder ? 'pcm' : 'blob';

  logger('info', `Web Worker Audio Actor initializing in ${mode} mode`, {
    useAudioEncoder,
    hasAudioEncoder: typeof AudioEncoder !== 'undefined',
  });

  // Create a new worker instance for this recording session
  const audioEncoderWorker = new Worker(
    new URL('../workers/audio-encoder.worker.ts', import.meta.url),
    {
      type: 'module',
      name: 'audio-encoder-worker',
    },
  );

  let audioSource: AudioSource | null = null;

  let mediaStream: MediaStream | null = null;

  let numAudioPacketsReceived = 0;
  let totalBytesReceived = 0;

  let recordingStarted = false;
  let isMuted = false;
  let muteStartTime: number | null = null;

  let isFinishedStop = false;

  if (!db) {
    safeSendBack({
      type: 'recorder.error',
      error: new Error(
        'Dexie DB instance is required for web worker audio actor',
      ),
    });

    return;
  }

  const audioHealthCheck = new AudioHealthCheck({
    logger,
    getTotalBytesReceived: () => totalBytesReceived,
    getMediaStream: () => mediaStream,
    onHealthCheckFailed: (reason: string) => {
      sendBack({
        type: 'recorder.error',
        error: new Error(`Audio health check failed: ${reason}`),
      });
    },
    getAdditionalLogData: () => ({
      hasAudioSource: Boolean(audioSource),
      numAudioPacketsReceived,
      isMuted,
      muteStartTime,
      recordingStarted,
      mode,
    }),
    isHealthy: () => {
      const hasLiveAudioTracks = mediaStream
        ?.getAudioTracks()
        .some((t) => t.readyState === 'live' && t.enabled);

      return hasLiveAudioTracks
        ? { healthy: true }
        : { healthy: false, reason: 'No live audio tracks' };
    },
  });

  // Callback for audio sources to report bytes received
  const onBytesReceived = (bytes: number) => {
    numAudioPacketsReceived += 1;
    totalBytesReceived += bytes;
  };

  const workerMessageHandler = (e: MessageEvent<MsgToMain>) => {
    const msg = e.data;

    if (msg.type === 'ready') {
      logger('info', 'Worker ready');
      recordingStarted = true;
    } else if (msg.type === 'rolling-to-new-chunk') {
      safeSendBack({
        type: 'fileSystem.rollingToNewChunk',
        completedChunkName: msg.completedChunkName,
      });
    } else if (msg.type === 'chunk-started') {
      const { fileName, chunkNum } = msg;
      logger('info', 'Chunk started', { fileName, chunkDurationInSeconds });

      db.appointmentFiles
        .add({
          id: msg.fileName,
          appointmentId,
          attemptCount: 0,
          lastUploadAttemptedAt: new Date().toISOString(),
          wasDegraded: false,
          shouldBeTranscribed: true,
          uploadType: isAudioFileChunkFile(msg.fileName)
            ? AppointmentFileTypesLookup.CHUNKED_AUDIO
            : AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
        })
        .then(() =>
          logger('info', `Added file '${msg.fileName}' to DB`, {
            fileName: msg.fileName,
          }),
        )
        .catch((err) => {
          // TODO(jesse)[ENG-1563]: Better error handling but this should be picked
          // up again by the periodic sync and reconciliation logic
          logger('warn', 'Error adding chunk to DB:', err);
        });

      safeSendBack({
        type: 'fileSystem.ready',
      });

      safeSendBack({
        type: 'fileSystem.fileCreated',
        fileName,
        chunkNum,
      });
    } else if (msg.type === 'chunk-finalized') {
      const { chunkNum, fileName } = msg;
      safeSendBack({
        type: 'fileSystem.fileReady',
        fileName,
        chunkNum,
      });
    } else if (msg.type === 'error') {
      safeSendBack({
        type: 'recorder.error',
        error: new Error(msg.message),
      } satisfies MediaRecorderEvents);
    }
  };

  const onMicrophoneDisconnectDetected = () => {
    logger('warn', 'Audio track ended', { deviceId });
    safeSendBack({ type: 'microphone.disconnected' });
  };

  const handleMuteEvent = () => {
    isMuted = true;
    muteStartTime = Date.now();

    logger('warn', 'Microphone is muted.', {
      deviceId,
      totalBytesReceived: totalBytesReceived,
      numAudioPacketsReceived: numAudioPacketsReceived,
    });
    safeSendBack({ type: 'microphone.muted' });
  };

  const handleUnmuteEvent = () => {
    isMuted = false;
    muteStartTime = null;
    logger('info', 'Microphone is unmuted.', {
      deviceId,
      totalBytesReceived: totalBytesReceived,
      numAudioPacketsReceived: numAudioPacketsReceived,
    });
    safeSendBack({ type: 'microphone.unmuted' });
  };

  const stopRecording = async () => {
    audioHealthCheck.stop();

    // 1. Stop the media stream FIRST - prevents new audio from entering
    try {
      mediaStream?.getTracks().forEach((track) => {
        track.removeEventListener('ended', onMicrophoneDisconnectDetected);
        track.stop();
      });
    } catch (e) {
      logger('warn', 'Error stopping media stream tracks', {
        error: e,
      });
    }

    // 2. Stop the audio source (handles mode-specific cleanup)
    if (audioSource) {
      await audioSource.stop();
      audioSource = null;
    }

    // 3. Wait for worker to finalize encoding - safe because no more data can arrive
    const workerStopped = new Promise<void>((resolve) => {
      const stoppedHandler = (e: MessageEvent<MsgToMain>) => {
        if (e.data.type === 'stopped') {
          logger('info', 'Worker stopped');
          audioEncoderWorker.removeEventListener('message', stoppedHandler);
          resolve();
        }
      };
      audioEncoderWorker.addEventListener('message', stoppedHandler);
    });

    try {
      audioEncoderWorker.postMessage({ type: 'stop' } satisfies MsgFromMain);
      await workerStopped;
    } catch (e) {
      logger('error', 'Error stopping worker', {
        error: e,
      });
    }

    // 4. Terminate worker to release resources
    try {
      audioEncoderWorker.terminate();
      logger('info', 'Worker terminated successfully');
    } catch (e) {
      logger('error', 'Error terminating worker', {
        error: e,
      });
    }

    logger('info', 'Finished web worker audio recording cleanup');

    audioEncoderWorker.removeEventListener('message', workerMessageHandler);
    isFinishedStop = true;

    safeSendBack({
      type: 'recorder.cleanup_finalized',
    });

    logger('info', 'Recording stopped, cleanup complete', {
      numAudioPacketsReceived: numAudioPacketsReceived,
      totalBytesReceived: totalBytesReceived,
    });
  };

  const startRecording = async () => {
    try {
      window.eluveMediaStreams.stop('sessionRecorder');
      audioEncoderWorker.addEventListener('message', workerMessageHandler);

      // Getting the media stream from the audio input device
      mediaStream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
      });

      // Validate MediaStream tracks
      const audioTracks = mediaStream.getAudioTracks();
      if (audioTracks.length === 0) {
        throw new Error('No audio tracks available in MediaStream');
      }

      audioTracks.forEach((track) => {
        if (!track.enabled || track.readyState !== 'live') {
          logger('warn', 'Audio track is not active', {
            enabled: track.enabled,
            readyState: track.readyState,
            label: track.label,
          });
        }
        track.addEventListener('ended', onMicrophoneDisconnectDetected);
      });

      const audioSourceConfig: AudioSourceConfig = {
        mediaStream,
        worker: audioEncoderWorker,
        logger,
        MuteDetector,
        deviceId,
        channelCount,
        onMute: handleMuteEvent,
        onUnmute: handleUnmuteEvent,
        onBytesReceived,
        audioPacketSubject,
      };

      // Create and initialize audio source based on mode
      if (mode === 'pcm') {
        audioSource = new PcmAudioSource(audioSourceConfig);
      } else {
        audioSource = new BlobAudioSource({
          ...audioSourceConfig,
          mimeType: supportedAudioFormat.mimeType,
        });
      }

      // Initialize audio source and get actual audio parameters
      const { sampleRate, channels } = await audioSource.initialize();

      recordingStarted = true;

      const loggingEnv = datadogEnv ?? 'local-dev';
      const isLocalDev = loggingEnv === 'local-dev';

      // Initialize the worker
      audioEncoderWorker.postMessage({
        type: 'init',
        mode,
        dirPath: [
          userId,
          tenantId,
          ...(appointmentId ? ['appointments', appointmentId] : []),
        ],
        chunking: chunkDurationInSeconds ? { chunkDurationInSeconds } : false,
        opusBitrate: 64_000,
        sampleRate,
        channels,
        recordingStartedAt,
        initialFileName,
        resumableUploadSessionUri,
        nextChunkResumableUploadSessionUri,
        loggingConfig: isLocalDev
          ? { type: 'loki' }
          : {
              type: 'datadog',
              clientToken: 'pub1f5d27dc59ce54610ad0852ff58ac9f2',
              site: 'us5.datadoghq.com',
              service: serviceName ?? 'web-vite',
              env: loggingEnv,
            },
        tenantId,
        userId,
        appointmentId,
      } satisfies MsgFromMain);

      window.eluveMediaStreams.set('sessionRecorder', mediaStream);

      audioHealthCheck.start();

      safeSendBack({
        type: 'recorder.started',
        mediaStream,
      });
    } catch (error) {
      // When the selected device ID is not available, treat it as a disconnection
      if (error instanceof Error && error.name === 'OverconstrainedError') {
        const isDeviceIdConstraint =
          ('constraints' in error && error.constraints === 'deviceId') ||
          ('constraint' in error && error.constraint === 'deviceId');
        if (isDeviceIdConstraint) {
          onMicrophoneDisconnectDetected();
          return;
        }
      }

      safeSendBack({
        type: 'recorder.error',
        error: new Error(
          `An error occurred while getting stream from audio input device. Error: ${(error as Error)?.message}`,
        ),
      });
    }
  };

  startRecording();

  receive((event) => {
    if (event.type === 'next_chunk_resumable_upload_session_uri_available') {
      audioEncoderWorker.postMessage(event satisfies MsgFromMain);
    }
    if (event.type === 'recorder.stop') {
      stopRecording();
      return;
    }
  });

  return () => {
    if (!isFinishedStop) {
      logger(
        'warn',
        'Web Worker Audio Actor cleanup invoked without stop. This could indicate a race condition',
      );
      stopRecording();
    }
  };
});
